import compact from 'lodash-es/compact';
import isEmpty from 'lodash-es/isEmpty';
import uniq from 'lodash-es/uniq';
import {
  exactMatchInsurancePlans,
  insurerStatesMap,
  partialInsurancePlans,
  statesMapping,
} from '../constants';
import {
  GetInsurersByPlanNameResult,
  IdentificationMethod,
  MappedInsurers,
  MappedInsurersValues,
  SupportedInsurers,
} from '../types';
import { Insurer } from '@getvim-os/standard';
import { logger } from '@getvim/vim-connect-logger';

const patientMappingLogger = logger.scope('patient mapping');

let matchedTerms = {};

// replace(/\s+/g, ' ') - leaves one space and removes duplicates and special chars
const formatWithSpaces = (stringToFormat: string) =>
  stringToFormat.replace(/\s+/g, ' ').toLowerCase();

// replace(/\s/g, '') - removes all spaces and special chars
const formatWithoutSpaces = (stringToFormat: string) =>
  stringToFormat.replace(/\s/g, '').toLowerCase();

export function getInsurersByPlanName({
  ehrInsurance,
  patientAddressState,
}: {
  ehrInsurance: string | number | undefined;
  patientAddressState: string | undefined;
}): GetInsurersByPlanNameResult {
  matchedTerms = {};

  if (!ehrInsurance) return { potentialInsurers: [] };
  const ehrInsuranceString = ehrInsurance.toString();
  const exactMatchedInsurer = lookForExactMatch(ehrInsuranceString);

  if (exactMatchedInsurer) {
    return {
      potentialInsurers: [exactMatchedInsurer],
      identificationMethod: IdentificationMethod.EXACT,
      identificationTerms: { [exactMatchedInsurer]: [ehrInsuranceString] },
    };
  }

  const containedInsurers = lookForPartialMatch(ehrInsuranceString);
  const convertedInsurers = containedInsurers.flatMap((insurer) => {
    if (insurer !== MappedInsurers.BCBS) return [insurer];

    const stateNameInInsurance = getStateFromInsurance(ehrInsuranceString);
    const stateToUse = stateNameInInsurance ?? patientAddressState;

    return getInsurerByState(stateToUse) ?? [];
  });

  const possibleInsurers = uniq(compact(convertedInsurers));

  let identificationMetadata = {};
  if (!isEmpty(possibleInsurers)) {
    identificationMetadata = {
      identificationMethod: IdentificationMethod.PARTIAL,
      identificationTerms: matchedTerms,
    };
  }

  return {
    potentialInsurers: possibleInsurers,
    ...identificationMetadata,
  };
}

export function getSupportedInsurer(
  mappedInsurer: MappedInsurersValues,
): SupportedInsurers | undefined {
  const mappedInsurerKey = Object.keys(MappedInsurers).find((key) => {
    return MappedInsurers[key] === mappedInsurer;
  });

  return SupportedInsurers[mappedInsurerKey as keyof SupportedInsurers];
}

export function getInsurersByInsuranceName(
  ehrInsurance: string | undefined,
  patientAddressState: string | undefined,
): Insurer[] | undefined {
  const insurersByPlanName = getInsurersByPlanName({ ehrInsurance, patientAddressState });

  const possibleInsurers: Insurer[] = compact(
    insurersByPlanName.potentialInsurers.map((insurer) => getSupportedInsurer(insurer)),
  );

  return possibleInsurers;
}

function lookForExactMatch(insurancePlan: string): MappedInsurersValues | undefined {
  const formattedInsurancePlan = formatWithSpaces(insurancePlan);
  let insurerToReturn: MappedInsurersValues | undefined;

  for (const [insurer, plans] of Object.entries(exactMatchInsurancePlans)) {
    const exactPlan = plans.some((plan) => formatWithSpaces(plan) === formattedInsurancePlan);

    if (exactPlan) {
      insurerToReturn = insurer;

      break;
    }
  }

  return insurerToReturn;
}

function lookForPartialMatch(insurancePlan: string): MappedInsurersValues[] {
  const formattedInsurancePlan = formatWithoutSpaces(insurancePlan);
  const insurersToReturn: MappedInsurersValues[] = [];

  for (const [insurer, plans] of Object.entries(partialInsurancePlans)) {
    const matchingPlans = plans.filter((plan) =>
      formattedInsurancePlan.includes(formatWithoutSpaces(plan)),
    );

    if (matchingPlans.length) {
      matchedTerms[insurer] = matchingPlans;
      insurersToReturn.push(insurer as MappedInsurersValues);
    }
  }

  return insurersToReturn;
}

function getStateFromInsurance(insurancePlan: string): string | undefined {
  const formattedInsurancePlan = ` ${formatWithSpaces(insurancePlan)} `;
  const relevantStates: string[] = [];

  for (const [twoLetterStateName, stateFullName] of Object.entries(statesMapping)) {
    if (
      formattedInsurancePlan.includes(` ${twoLetterStateName.toLowerCase()} `) ||
      formattedInsurancePlan.includes(` ${stateFullName.toLowerCase()} `)
    )
      relevantStates.push(twoLetterStateName);
  }

  return relevantStates[0];
}

function getInsurerByState(state?: string): MappedInsurersValues[] | undefined {
  return state?.length ? insurerStatesMap[state.toUpperCase()] : undefined;
}

export function findInsurerEnum(source?: string): Insurer | undefined {
  return Object.values(Insurer).find((value) => value === source);
}
